// Smart Savings Coach card on /insights.
//
// Auto-loads on mount (no button click required) and shows 3-5 concrete
// money-saving recommendations from the coach AI. Each card is one
// suggestion the user can discuss with the assistant to verify and plan,
// or "Dismiss" (suppressed in future prompts via the
// ai_coach_actions table).
//
// Backend caches output for 7 days with a stale-on-read background refresh,
// so first paint usually serves a cached array immediately and a refreshing
// pill confirms a new generation is in flight.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspace';
import { formatCurrency } from '@/lib/utils';
import { toast } from '@/lib/toast';

type CoachAction =
  | 'cancel_subscription'
  | 'reduce_category'
  | 'switch_merchant'
  | 'increase_savings';

interface CoachAdviceItem {
  title: string;
  detail: string;
  monthly_savings_aud: number;
  category: string;
  action: CoachAction;
}

interface CoachResponse {
  advice: CoachAdviceItem[];
  generated_at: number | null;
  model: string | null;
  language: 'zh' | 'en';
  refreshing: boolean;
  cache_age_hours: number | null;
  cache_fresh_hours: number;
}

const ACTION_ICONS: Record<CoachAction, string> = {
  cancel_subscription: '✂️',
  reduce_category: '📉',
  switch_merchant: '🔄',
  increase_savings: '🐷',
};

interface Props {
  currency: string;
}

export function CoachCard({ currency }: Props) {
  const { t, i18n } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);
  const qc = useQueryClient();
  const navigate=useNavigate();
  const language: 'zh' | 'en' = i18n.language.startsWith('zh') ? 'zh' : 'en';

  // Locally hide only items the user has dismissed for instant
  // feedback. The server-side refresh strips them from future prompts but
  // the current cached batch still contains them until next refresh.
  const [hiddenTitles, setHiddenTitles] = useState<Set<string>>(new Set());

  const coachQuery = useQuery({
    queryKey: ['insights', 'coach', ws?.id, language],
    queryFn: () => api.get<CoachResponse>(`/insights/coach?language=${language}`),
    enabled: !!ws,
    staleTime: 0,
    refetchOnMount: 'always',
    // Poll while the server is mid-refresh so the new advice shows up
    // without a manual reload (typically Gemini returns in 10-20 s).
    refetchInterval: (q) => (q.state.data?.refreshing ? 5000 : false),
  });

  const refreshMutation = useMutation({
    mutationFn: () => api.post<CoachResponse>(`/insights/coach/refresh`, { language }),
    onSuccess: (data) => {
      qc.setQueryData(['insights', 'coach', ws?.id, language], data);
      setHiddenTitles(new Set());
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('insights.coachError')),
  });

  const adoptMutation = useMutation({
    mutationFn: (item: CoachAdviceItem) =>
      api.post<{ ok: boolean; action_url: string }>(`/insights/coach/adopt`, {
        advice_title: item.title,
        advice_detail: item.detail,
        monthly_savings_aud: item.monthly_savings_aud,
        category: item.category,
        language,
      }),
    onSuccess: (data) => navigate(data.action_url),
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('insights.coachError'));
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (item: CoachAdviceItem) =>
      api.post<{ ok: boolean }>(`/insights/coach/dismiss`, {
        advice_title: item.title,
        monthly_savings_aud: item.monthly_savings_aud,
      }),
    onMutate: (item) => {
      setHiddenTitles((prev) => new Set(prev).add(item.title));
    },
    onSuccess: () => {
      toast.success(t('insights.coachDismissedToast'));
    },
    onError: (err, item) => {
      setHiddenTitles((prev) => {
        const next = new Set(prev);
        next.delete(item.title);
        return next;
      });
      toast.error(err instanceof ApiError ? err.message : t('insights.coachError'));
    },
  });

  const data = coachQuery.data;
  const allAdvice = data?.advice ?? [];
  const advice = allAdvice.filter((a) => !hiddenTitles.has(a.title));
  const isLoading = coachQuery.isPending && !data;
  const isRefreshing = data?.refreshing === true || refreshMutation.isPending;

  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">💡 {t('insights.coachTitle')}</div>
          <div className="text-xs text-muted-foreground">
            {t('insights.coachSubtitle')}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{language==='zh'?'建议与金额均待核实，先查依据再制定方案。':'Suggestions and amounts need verification before making a plan.'}</p>
        </div>
        <div className="flex items-center gap-2">
          {isRefreshing && advice.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              {t('insights.coachRefreshing')}
            </span>
          )}
          {data?.generated_at && (
            <span className="text-xs text-muted-foreground">
              {t('insights.coachGeneratedAgo', {
                days: Math.max(0, Math.floor((data.cache_age_hours ?? 0) / 24)),
              })}
            </span>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="py-8 text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
          <div className="text-xs text-muted-foreground">{t('insights.coachLoading')}</div>
        </div>
      )}

      {!isLoading && advice.length === 0 && isRefreshing && (
        <div className="py-8 text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
          <div className="text-xs text-muted-foreground">{t('insights.coachLoading')}</div>
        </div>
      )}

      {!isLoading && advice.length === 0 && !isRefreshing && (
        <div className="py-6 text-center text-sm text-muted-foreground">
          {t('insights.coachEmpty')}
        </div>
      )}

      {advice.length > 0 && (
        <>
          {/* Vertical stack on mobile (was horizontal snap-scroll — user
              feedback 2026-05-18: hard to discover other cards, wastes
              vertical space, awkward to swipe). Desktop keeps the 2-3 col
              grid since horizontal real estate is plentiful there. */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {advice.map((item) => (
              <AdviceCardItem
                key={item.title}
                item={item}
                currency={currency}
                onAdopt={() => adoptMutation.mutate(item)}
                onDismiss={() => dismissMutation.mutate(item)}
                isAdopting={
                  adoptMutation.isPending && adoptMutation.variables?.title === item.title
                }
              />
            ))}
          </div>
        </>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {data?.model
            ? `${data.model.replace('gemini-', '')}`
            : t('insights.coachAutoUpdate')}
        </span>
        <button
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending || isRefreshing}
          className="rounded-md px-2 py-1 hover:bg-muted disabled:opacity-50"
        >
          {refreshMutation.isPending ? '…' : t('insights.coachRegenerate')}
        </button>
      </div>
    </div>
  );
}

function AdviceCardItem(props: {
  item: CoachAdviceItem;
  currency: string;
  onAdopt: () => void;
  onDismiss: () => void;
  isAdopting: boolean;
}) {
  const { t,i18n } = useTranslation();
  const { item } = props;

  return (
    <div className="flex flex-col rounded-xl border bg-background p-3">
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-lg leading-none">{ACTION_ICONS[item.action] ?? '💡'}</span>
          <span className="text-xs rounded-full bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">
            {t(`insights.coachAction.${item.action}`)}
          </span>
        </div>
        <button
          onClick={props.onDismiss}
          aria-label={t('insights.coachDismiss')}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <svg
            className="h-3.5 w-3.5"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 5l10 10M15 5l-10 10" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <h3 className="mb-1 text-sm font-semibold leading-snug">{item.title}</h3>
      {/* No line-clamp: coach.detail is capped at 500 chars server-side and
          is the single most-valuable piece of info per card. Variable card
          heights are acceptable; the responsive grid still tiles correctly. */}
      <p className="mb-3 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
        {item.detail}
      </p>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2">
        <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold tabular-nums text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
          {i18n.language.startsWith('zh')?`AI 估计 ${formatCurrency(item.monthly_savings_aud, props.currency)}/月（待核实）`:`AI estimate ${formatCurrency(item.monthly_savings_aud, props.currency)}/mo · unverified`}
        </span>
        <button
          onClick={props.onAdopt}
          disabled={props.isAdopting}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {props.isAdopting ? '…' : i18n.language.startsWith('zh')?'讨论执行方案':'Discuss plan'}
        </button>
      </div>
    </div>
  );
}
